/**
 * Page context sent with every copilot WS turn (route, screen, in-app selection).
 * Selection and bind targets are scoped to the current screen so stale session
 * storage (e.g. part_data after leaving Parts) does not stick on Inspection Stations.
 */
import { getWorkstationData, getSelectedUsecase, getLocalSelectedPart } from '../../../helpers/storage';

export type CopilotScreen =
  | 'part_data'
  | 'annotation'
  | 'training'
  | 'usecase_list'
  | 'workstation_list'
  | 'part_list'
  | 'home'
  | '';

export interface CopilotStarterChip {
  id: string;
  /** Shown on Home / empty-state cards */
  label: string;
  /** Sent to Copilot when the card is tapped (defaults to label) */
  prompt?: string;
  /** Opens this Help Center article in the Help tab */
  helpPath?: string;
  primary?: boolean;
}

/** Search/chat starters for informational turns. */
export const SEARCH_STARTER_CHIPS: Record<string, CopilotStarterChip[]> = {
  part_data: [
    { id: 'ask_question', label: 'Summarize this part\u2019s dataset', primary: true },
    { id: 'ask_question', label: 'What\u2019s blocking training?' },
  ],
  annotation: [
    { id: 'ask_question', label: 'How complete is annotation?', primary: true },
  ],
  training: [
    { id: 'ask_question', label: 'Summarize training status', primary: true },
    { id: 'check_training', label: 'Check training progress' },
  ],
  usecase_list: [
    { id: 'ask_question', label: 'Summarize this inspector', primary: true },
  ],
  workstation_list: [
    { id: 'ask_question', label: 'What inspectors are on this station?' },
  ],
};

export const ROUTE_STARTER_CHIPS: Record<string, CopilotStarterChip[]> = {
  part_data: [
    { id: 'analyze_data', label: 'Analyze dataset on this part', primary: true },
    { id: 'guide_upload', label: 'Upload good images' },
    { id: 'start_training', label: 'Start training' },
  ],
  annotation: [
    { id: 'analyze_data', label: 'Check annotation progress', primary: true },
    { id: 'guide_upload', label: 'Upload more images' },
  ],
  training: [
    { id: 'start_training', label: 'Start or resume training', primary: true },
    { id: 'analyze_data', label: 'Check if data is ready' },
  ],
  usecase_list: [
    { id: 'add_usecase', label: 'Create AI Inspector', primary: true },
    { id: 'analyze_data', label: 'Analyze inspector data' },
  ],
  workstation_list: [
    { id: 'create_station', label: 'Set up Inspection Station', primary: true },
    { id: 'add_usecase', label: 'Create AI Inspector' },
  ],
  part_list: [
    { id: 'add_part', label: 'Add a part', primary: true },
    { id: 'guide_upload', label: 'Upload training images' },
  ],
};

function safeParse(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function resolveCopilotScreen(pathname: string): CopilotScreen {
  const p = (pathname || '').toLowerCase();
  if (p.includes('annotation')) return 'annotation';
  if (p.includes('training') || p.includes('experiment')) return 'training';
  if (p.includes('part-data') || p.includes('part_data') || p.includes('manage-data') || p.includes('data-management')) {
    return 'part_data';
  }
  if (p.includes('usecase') || p.includes('inspector')) return 'usecase_list';
  if (p.includes('workstation') || p.includes('station')) return 'workstation_list';
  if (p.includes('/part') || p.includes('parts')) return 'part_list';
  return '';
}

type RawSelection = {
  workstation: Record<string, unknown> | null;
  usecase: Record<string, unknown> | null;
  part: Record<string, unknown> | null;
  workstation_id: string;
  usecase_id: string;
  part_id: string;
};


function extractRouteEntityIds(pathname: string): {
  station_id: string;
  usecase_id: string;
  part_id: string;
} {
  const p = (pathname || '').toLowerCase();
  const station = p.match(/\/usecase\/list\/([a-f0-9]{24})/i);
  const usecase = p.match(/\/part\/list\/([a-f0-9]{24})/i);
  const part = p.match(/\/part\/data\/([a-f0-9]{24})/i);
  return {
    station_id: station?.[1] || '',
    usecase_id: usecase?.[1] || '',
    part_id: part?.[1] || '',
  };
}

/** Prefer current URL over stale session/local storage when IDs disagree. */
function reconcileSelectionWithRoute(screen: CopilotScreen, raw: RawSelection): RawSelection {
  if (typeof window === 'undefined') return raw;
  const routeIds = extractRouteEntityIds(window.location.pathname);
  let { workstation, usecase, part, workstation_id, usecase_id, part_id } = raw;

  // List pages: only entities implied by the URL — not stale session storage.
  if (screen === 'workstation_list') {
    return {
      workstation: null,
      usecase: null,
      part: null,
      workstation_id: '',
      usecase_id: '',
      part_id: '',
    };
  }

  if (screen === 'usecase_list') {
    if (routeIds.station_id) {
      if (workstation_id && workstation_id !== routeIds.station_id) {
        workstation = null;
      }
      workstation_id = routeIds.station_id;
      if (!workstation) {
        workstation = {
          _id: routeIds.station_id,
          id: routeIds.station_id,
          workstation_id: routeIds.station_id,
        };
      }
      return {
        workstation,
        usecase: null,
        part: null,
        workstation_id,
        usecase_id: '',
        part_id: '',
      };
    }
    return {
      workstation: null,
      usecase: null,
      part: null,
      workstation_id: '',
      usecase_id: '',
      part_id: '',
    };
  }

  if (screen === 'part_list') {
    if (routeIds.usecase_id) {
      if (usecase_id && usecase_id !== routeIds.usecase_id) {
        usecase = null;
      }
      usecase_id = routeIds.usecase_id;
      if (!usecase) {
        usecase = {
          _id: routeIds.usecase_id,
          id: routeIds.usecase_id,
          usecase_id: routeIds.usecase_id,
        };
      }
      return {
        workstation,
        usecase,
        part: null,
        workstation_id,
        usecase_id,
        part_id: '',
      };
    }
    return {
      workstation: null,
      usecase: null,
      part: null,
      workstation_id: '',
      usecase_id: '',
      part_id: '',
    };
  }

  if (routeIds.station_id) {
    if (workstation_id && workstation_id !== routeIds.station_id) {
      workstation = null;
    }
    workstation_id = routeIds.station_id;
  }

  if (routeIds.usecase_id) {
    if (usecase_id && usecase_id !== routeIds.usecase_id) {
      usecase = null;
    }
    usecase_id = routeIds.usecase_id;
  }

  if (routeIds.part_id) {
    if (part_id && part_id !== routeIds.part_id) {
      part = null;
    }
    part_id = routeIds.part_id;
    if (!part) {
      part = { _id: routeIds.part_id, id: routeIds.part_id, part_id: routeIds.part_id };
    }
  }

  if (routeIds.usecase_id && !usecase) {
    usecase = { _id: routeIds.usecase_id, id: routeIds.usecase_id, usecase_id: routeIds.usecase_id };
    usecase_id = routeIds.usecase_id;
  }

  if (routeIds.station_id && !workstation) {
    workstation = {
      _id: routeIds.station_id,
      id: routeIds.station_id,
      workstation_id: routeIds.station_id,
    };
    workstation_id = routeIds.station_id;
  }

  if (screen === 'part_data' && !routeIds.part_id) {
    part = null;
    part_id = '';
  }

  return { workstation, usecase, part, workstation_id, usecase_id, part_id };
}


function readRawSelection(): RawSelection {
  let workstation: Record<string, unknown> | null = null;
  let usecase: Record<string, unknown> | null = null;
  let part: Record<string, unknown> | null = null;

  try {
    workstation = getWorkstationData();
  } catch {
    workstation = safeParse(sessionStorage.getItem('station_data'));
  }
  try {
    usecase = getSelectedUsecase();
  } catch {
    usecase = safeParse(sessionStorage.getItem('usecase_data'));
  }
  try {
    part = getLocalSelectedPart();
  } catch {
    part = safeParse(sessionStorage.getItem('part_data'));
  }

  const workstation_id = String(
    workstation?._id || workstation?.id || workstation?.workstation_id || '',
  );
  const usecase_id = String(
    usecase?._id || usecase?.id || usecase?.usecase_id || '',
  );
  const part_id = String(
    part?._id || part?.id || part?.part_id || '',
  );

  return { workstation, usecase, part, workstation_id, usecase_id, part_id };
}

function scopeSelectionForScreen(screen: CopilotScreen, raw: RawSelection): {
  selection: Record<string, unknown>;
  workstation_id: string;
  usecase_id: string;
  part_id: string;
} {
  const selection: Record<string, unknown> = {};
  let workstation_id = '';
  let usecase_id = '';
  let part_id = '';

  const includeWs = (s: CopilotScreen) =>
    s === 'workstation_list' || s === 'usecase_list' || s === 'part_list'
    || s === 'part_data' || s === 'annotation' || s === 'training';
  const includeUc = (s: CopilotScreen) =>
    s === 'usecase_list' || s === 'part_list' || s === 'part_data'
    || s === 'annotation' || s === 'training';
  const includePart = (s: CopilotScreen) =>
    s === 'part_list' || s === 'part_data' || s === 'annotation';

  if (includeWs(screen) && raw.workstation) {
    selection.workstation = raw.workstation;
    workstation_id = raw.workstation_id;
  }
  if (includeUc(screen) && raw.usecase) {
    selection.usecase = raw.usecase;
    usecase_id = raw.usecase_id;
  }
  if (includePart(screen) && (raw.part || raw.part_id)) {
    if (raw.part) selection.part = raw.part;
    part_id = raw.part_id;
  }

  if (includeUc(screen) && raw.usecase_id && !selection.usecase) {
    selection.usecase = raw.usecase || { _id: raw.usecase_id, id: raw.usecase_id };
    usecase_id = raw.usecase_id;
  }

  if (includeWs(screen) && raw.workstation_id && !selection.workstation) {
    selection.workstation = raw.workstation || {
      _id: raw.workstation_id,
      id: raw.workstation_id,
    };
    workstation_id = raw.workstation_id;
  }

  return { selection, workstation_id, usecase_id, part_id };
}


const SCREEN_TITLES: Record<string, string> = {
  part_data: 'Data Management',
  annotation: 'Annotation',
  training: 'Model Training',
  usecase_list: 'AI Inspectors',
  workstation_list: 'Inspection Stations',
  part_list: 'Parts',
};

function pickEntityName(obj: Record<string, unknown> | undefined, ...keys: string[]): string {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function buildScreenSummary(
  screen: CopilotScreen,
  selection: Record<string, unknown>,
  dataset_stats: Record<string, unknown>,
  active_tab: string,
  collection_mode: string,
): string {
  const page = SCREEN_TITLES[screen] || (screen || 'LIVIS');
  const bits: string[] = [`The user is viewing the ${page} page in LIVIS`];
  const ws = selection.workstation as Record<string, unknown> | undefined;
  const uc = selection.usecase as Record<string, unknown> | undefined;
  const part = selection.part as Record<string, unknown> | undefined;
  const wsName = pickEntityName(ws, 'workstation_name', 'name');
  const ucName = pickEntityName(uc, 'usecase_name', 'name');
  const partName = pickEntityName(part, 'part_name', 'name', 'part_number');
  if (screen === 'workstation_list' && !wsName) {
    bits.push('browsing all inspection stations (no single station is selected in page context)');
  } else if (screen === 'usecase_list' && wsName && !ucName) {
    bits.push(`viewing AI Inspectors for inspection station "${wsName}"`);
  } else if (screen === 'part_list' && ucName && !partName) {
    bits.push(`viewing parts for AI Inspector "${ucName}"`);
  }
  if (wsName && screen !== 'workstation_list') bits.push(`inspection station "${wsName}"`);
  if (ucName && !(screen === 'part_list' && !partName)) bits.push(`AI Inspector "${ucName}"`);
  if (partName) bits.push(`part "${partName}"`);
  if (screen === 'part_data') {
    const ok = Number(dataset_stats.ok_count) || 0;
    const ng = Number(dataset_stats.ng_count) || 0;
    const total = Number(dataset_stats.total_images) || 0;
    if (total || ok || ng) bits.push(`dataset shows ${ok} OK and ${ng} NG images (${total} total)`);
    if (collection_mode) bits.push(`collection mode ${collection_mode}`);
    if (active_tab) bits.push(`active tab "${active_tab}"`);
  }
  if (screen === 'training') bits.push('training progress and experiment controls are visible');
  if (screen === 'annotation') bits.push('annotation and labelling tools are visible');
  return `${bits.join('; ')}.`;
}


export function getCopilotUiContext(): {
  route: string;
  screen: string;
  selection: Record<string, unknown>;
  collection_mode: string;
  active_tab: string;
  dataset_stats: Record<string, unknown>;
  workstation_id: string;
  usecase_id: string;
  part_id: string;
  training_job_id: string;
  screen_summary: string;
} {
  const route = typeof window !== 'undefined'
    ? `${window.location.pathname}${window.location.search || ''}`
    : '';
  const screen = resolveCopilotScreen(route);
  const raw = reconcileSelectionWithRoute(screen, readRawSelection());
  const scoped = scopeSelectionForScreen(screen, raw);

  let collection_mode = '';
  let active_tab = '';
  const dataset_stats: Record<string, unknown> = {};
  if (screen === 'part_data' && typeof window !== 'undefined') {
    collection_mode = sessionStorage.getItem('collection_mode') || '';
    active_tab = sessionStorage.getItem('active_tab') || '';
    const okRaw = sessionStorage.getItem('ok_count');
    const ngRaw = sessionStorage.getItem('ng_count');
    if (okRaw) dataset_stats.ok_count = Number(okRaw) || 0;
    if (ngRaw) dataset_stats.ng_count = Number(ngRaw) || 0;
    const totalRaw = sessionStorage.getItem('total_images');
    if (totalRaw) dataset_stats.total_images = Number(totalRaw) || 0;
  }

  let training_job_id = '';
  if (typeof window !== 'undefined' && screen === 'training') {
    training_job_id = sessionStorage.getItem('training_job_id') || sessionStorage.getItem('experiment_id') || '';
  }

  const screen_summary = buildScreenSummary(screen, scoped.selection, dataset_stats, active_tab, collection_mode);

  return {
    route,
    screen,
    screen_summary,
    selection: scoped.selection,
    collection_mode,
    active_tab,
    dataset_stats,
    workstation_id: scoped.workstation_id,
    usecase_id: scoped.usecase_id,
    part_id: scoped.part_id,
    training_job_id,
  };
}

export function getRouteStarterChips(screen: string): CopilotStarterChip[] {
  const action = ROUTE_STARTER_CHIPS[screen] || [];
  const search = SEARCH_STARTER_CHIPS[screen] || [];
  const seen = new Set<string>();
  const merged: CopilotStarterChip[] = [];
  for (const c of [...search, ...action]) {
    const key = `${c.id}:${c.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(c);
  }
  return merged;
}

/** Home tab quick actions only (agent intents), max three per screen. */
export function getRouteHomeActionChips(screen: string): CopilotStarterChip[] {
  return (ROUTE_STARTER_CHIPS[screen] || []).slice(0, 3);
}

const HOME_PAGE_HELP_LABEL: Record<string, string> = {
  part_data: 'part data',
  annotation: 'annotation',
  training: 'model training',
  usecase_list: 'ai inspectors',
  workstation_list: 'inspection stations',
  part_list: 'parts',
};

/** Section heading above Home quick-action cards, e.g. "Help on inspection stations". */
export function routeHelpOnPageTitle(screen: string): string {
  const topic = HOME_PAGE_HELP_LABEL[screen];
  return topic ? `Help on ${topic}` : '';
}

export function getCopilotWorkBinding(): {
  bound_type: 'inspector' | 'part' | 'training';
  bound_id: string;
  bound_label: string;
} | null {
  const ctx = getCopilotUiContext();
  const screen = ctx.screen;
  const sel = ctx.selection;

  if (screen === 'training' && ctx.usecase_id) {
    const uc = sel.usecase as Record<string, unknown> | undefined;
    return {
      bound_type: 'training',
      bound_id: ctx.training_job_id || ctx.usecase_id,
      bound_label: String(uc?.usecase_name || uc?.name || 'Training'),
    };
  }
  if ((screen === 'part_data' || screen === 'annotation' || screen === 'part_list') && ctx.part_id) {
    const part = sel.part as Record<string, unknown> | undefined;
    return {
      bound_type: 'part',
      bound_id: ctx.part_id,
      bound_label: String(part?.part_name || part?.name || part?.part_number || 'Part'),
    };
  }
  if ((screen === 'usecase_list' || screen === 'part_list') && ctx.usecase_id) {
    const uc = sel.usecase as Record<string, unknown> | undefined;
    return {
      bound_type: 'inspector',
      bound_id: ctx.usecase_id,
      bound_label: String(uc?.usecase_name || uc?.name || 'Inspector'),
    };
  }
  return null;
}

export interface RouteHomeSection {
  title: string;
  subtitle: string;
}

function _entityNames(selection: Record<string, unknown>) {
  const part = selection.part as Record<string, unknown> | undefined;
  const uc = selection.usecase as Record<string, unknown> | undefined;
  const ws = selection.workstation as Record<string, unknown> | undefined;
  return {
    partName: String(part?.part_name || part?.name || part?.part_number || '').trim(),
    ucName: String(uc?.usecase_name || uc?.name || '').trim(),
    wsName: String(
      ws?.workstation_name || ws?.name || ws?.station_name || '',
    ).trim(),
  };
}

/** Home + empty-chat headline for the current LIVIS Train screen. */
export function routeHomeSection(
  screen: string,
  selection: Record<string, unknown>,
): RouteHomeSection {
  const { partName, ucName, wsName } = _entityNames(selection);

  switch (screen) {
    case 'part_data':
      return {
        title: partName ? `Part data — ${partName}` : 'Part data',
        subtitle:
          'Analyze uploads, add good/NG images, or start training — tap a prompt to run it in chat.',
      };
    case 'annotation':
      return {
        title: partName ? `Annotation — ${partName}` : 'Annotation',
        subtitle:
          'Check labeling progress and what is still missing before you train.',
      };
    case 'training':
      return {
        title: ucName ? `Training — ${ucName}` : 'Model training',
        subtitle:
          'Start or resume training, check readiness, or monitor the active run.',
      };
    case 'usecase_list':
      return {
        title: ucName
          ? `AI Inspector — ${ucName}`
          : wsName
            ? `Inspectors — ${wsName}`
            : 'AI Inspectors',
        subtitle:
          'Create an inspector, review parts, or check whether data is ready to train.',
      };
    case 'workstation_list':
      return {
        title: wsName ? `Inspection station — ${wsName}` : 'Inspection stations',
        subtitle:
          'Set up a station, link inspectors, and organize where models run on the line.',
      };
    case 'part_list':
      return {
        title: ucName ? `Parts — ${ucName}` : 'Parts',
        subtitle:
          'Add a part, upload training images, or open data management for an existing part.',
      };
    default:
      return {
        title: 'Suggested prompts',
        subtitle: 'Tap a card to send it as your next message to Copilot.',
      };
  }
}

export function routeContextHeadline(
  screen: string,
  selection: Record<string, unknown>,
): string {
  if (!screen) return 'How can I help you today?';
  return routeHomeSection(screen, selection).title;
}

type HelpNavNode = {
  id?: string;
  title?: string;
  path?: string;
  subItems?: HelpNavNode[];
};

function _flattenHelpArticles(nodes: HelpNavNode[]): Array<{ title: string; path: string }> {
  const out: Array<{ title: string; path: string }> = [];
  for (const node of nodes) {
    if (node.path && node.title) {
      out.push({ title: node.title, path: node.path });
    }
    if (node.subItems?.length) {
      out.push(..._flattenHelpArticles(node.subItems));
    }
  }
  return out;
}

/** LIVIS Train articles from Help tab navigation (same source as Help list). */
function _livisTrainHelpArticles(
  helpNavigation: HelpNavNode[],
): Array<{ title: string; path: string }> {
  const trainSection = helpNavigation.find(
    (node) =>
      node.id === 'livis-train' ||
      (node.path || '').includes('/livis-train'),
  );
  if (trainSection?.subItems?.length) {
    return _flattenHelpArticles(trainSection.subItems);
  }
  return _flattenHelpArticles(helpNavigation).filter((a) =>
    a.path.includes('/livis-train'),
  );
}

const HOME_HELP_PATH_ORDER: Record<string, string[]> = {
  part_data: ['data-collection', 'model-training'],
  annotation: ['data-collection'],
  training: ['model-training', 'deployment-recipes'],
  usecase_list: ['ai-inspector', 'part-management'],
  workstation_list: ['inspection-station', 'ai-inspector'],
  part_list: ['part-management', 'data-collection'],
};

/** Card titles: "Help on …" (lowercase topic, matches product language). */
const HOME_HELP_TOPIC_LABEL: Record<string, string> = {
  'inspection-station': 'inspection stations',
  'ai-inspector': 'ai inspectors',
  'part-management': 'part management',
  'data-collection': 'data collection & annotation',
  'model-training': 'model training',
  'deployment-recipes': 'deployment recipes',
  'platform-overview': 'platform overview',
};

export function getHomeHelpPrompts(
  screen: string,
  helpNavigation: HelpNavNode[] = [],
): CopilotStarterChip[] {
  if (!screen) return [];
  const articles = _livisTrainHelpArticles(helpNavigation);
  const order = HOME_HELP_PATH_ORDER[screen] || ['platform-overview'];
  const chips: CopilotStarterChip[] = [];

  for (const key of order) {
    const article = articles.find((a) => a.path.includes(key));
    if (!article?.path) continue;
    const topic =
      HOME_HELP_TOPIC_LABEL[key] ||
      (article.title ? article.title.toLowerCase() : '');
    if (!topic) continue;
    chips.push({
      id: 'open_help',
      label: `Help on ${topic}`,
      helpPath: article.path,
    });
    if (chips.length >= 2) break;
  }
  return chips;
}
